import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Clock, MapPin, Users } from "lucide-react";

const CAFE_IMAGE = "https://images.unsplash.com/photo-1728221998562-1971a6f07b31?w=800&auto=format&fit=crop&q=80";
const TEA_IMAGE = "https://images.unsplash.com/photo-1542738415-8f61c88d4f5c?w=800&auto=format&fit=crop&q=80";

const values = [
  {
    title: "Quality First",
    description: "We use quality British back bacon, proper bread, and Yorkshire Tea. No shortcuts."
  },
  {
    title: "Fast Service",
    description: "We know your time matters. In, fed, out. That's the deal."
  },
  {
    title: "Honest Prices",
    description: "Fair prices for proper portions. We don't do fancy mark-ups."
  },
  {
    title: "Consistency",
    description: "Same great sarnie every time. That's what keeps people coming back."
  }
];

const AboutPage = () => {
  return (
    <div className="page-transition">
      {/* Hero Section */}
      <section className="bg-gradient-hero py-16 lg:py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-primary-foreground mb-4">
            About Us
          </h1>
          <p className="text-primary-foreground/80 text-lg max-w-xl mx-auto">
            No fuss. No frills. Just proper food, properly made.
          </p>
        </div>
      </section>

      {/* Main Story Section */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative animate-fade-in">
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-elevated">
                <img 
                  src={CAFE_IMAGE}
                  alt="Cozy café atmosphere" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Content */}
            <div className="animate-slide-in-right">
              <h2 className="font-display text-3xl lg:text-4xl font-bold text-foreground mb-6">
                The Story
              </h2>
              <div className="decorative-line mb-6" />
              
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Bacon & Brews started with a simple idea: make proper breakfast food 
                  for people who don't have time for nonsense.
                </p>
                <p>
                  We're not a café with exposed brick and flat whites. We're a takeaway 
                  that does bacon sandwiches and tea. That's it. We do it well, we do it 
                  fast, and we don't pretend to be something we're not.
                </p>
                <p>
                  Our customers are builders heading to site, commuters catching the 
                  early train, parents doing the school run, and locals who know what 
                  they want for breakfast. They don't need a menu with 47 options. 
                  They need good food, quickly.
                </p>
                <p className="font-medium text-foreground">
                  So that's what we give them. Every single day.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 lg:py-24 bg-secondary pattern-overlay">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-foreground mb-4">
              What We Stand For
            </h2>
            <div className="decorative-line mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {values.map((value, index) => (
              <Card 
                key={value.title} 
                className="bg-card border-border shadow-card animate-fade-in-up"
                style={{ animationDelay: `${0.1 * index}s` }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                      <CheckCircle className="h-5 w-5 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                        {value.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Tea Section */}
      <section className="py-16 lg:py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="order-2 lg:order-1 animate-fade-in">
              <h2 className="font-display text-3xl lg:text-4xl font-bold text-foreground mb-6">
                About the "Brews"
              </h2>
              <div className="decorative-line mb-6" />
              
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Let's be clear: when we say "brews", we mean <strong className="text-foreground">tea</strong>. 
                  Proper British tea. Yorkshire Tea, to be exact.
                </p>
                <p>
                  We've had a few people walk in expecting craft beer. They leave with 
                  a cuppa instead, and you know what? They're usually grateful for it.
                </p>
                <p>
                  There's nothing quite like a strong mug of tea to go with a bacon sarnie. 
                  It's the proper British breakfast experience. The kind your nan would approve of.
                </p>
                <p className="font-medium text-foreground italic">
                  "A brew" = a cup of tea. Always has been, always will be.
                </p>
              </div>
            </div>

            {/* Image */}
            <div className="order-1 lg:order-2 relative animate-slide-in-right">
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-elevated">
                <img 
                  src={TEA_IMAGE}
                  alt="Cup of tea" 
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Steam animation */}
              <div className="absolute top-8 left-1/2 transform -translate-x-1/2">
                <div className="steam" />
                <div className="steam" />
                <div className="steam" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info Cards */}
      <section className="py-16 lg:py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="bg-card border-border shadow-card text-center">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-full bg-wheat/20 flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-7 w-7 text-wheat" />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                  Opening Hours
                </h3>
                <p className="text-muted-foreground text-sm">
                  Every Day<br />
                  <span className="font-medium text-foreground">8am – 12pm</span>
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-card text-center">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-7 w-7 text-accent" />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                  Location
                </h3>
                <p className="text-muted-foreground text-sm">
                  59 Torquay Road<br />
                  Paignton, Devon<br />
                  TQ3 3DT
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-card text-center">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-full bg-tea/20 flex items-center justify-center mx-auto mb-4">
                  <Users className="h-7 w-7 text-tea" />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                  Who We Serve
                </h3>
                <p className="text-muted-foreground text-sm">
                  Builders, commuters,<br />
                  early risers, locals,<br />
                  and hungry folk
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-gradient-hero">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-primary-foreground mb-4">
            Come See Us
          </h2>
          <p className="text-primary-foreground/80 text-lg mb-8 max-w-xl mx-auto">
            We're on Torquay Road, Paignton. Pop in for a sarnie and a brew.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild variant="wheat" size="xl">
              <Link to="/order">Order Now</Link>
            </Button>
            <Button asChild variant="brandOutline" size="xl" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
              <Link to="/contact">Get Directions</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
